import React, { useState, useEffect, useMemo } from 'react';
import { BacklogEntry } from '../../types/reader';
import { History, X, Trash2, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';

export interface BacklogDrawerProps {
  isOpen: boolean;
  currentPage: number;
  backlog: BacklogEntry[];
  onClose: () => void;
  onJumpToPage: (pageNumber: number) => void;
  onClearBacklog: () => void;
}

export const BacklogDrawer: React.FC<BacklogDrawerProps> = ({
  isOpen,
  currentPage,
  backlog,
  onClose,
  onJumpToPage,
  onClearBacklog,
}) => {
  const [filterArchetype, setFilterArchetype] = useState<string>('all');
  const [expandedEntries, setExpandedEntries] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const toggleExpand = (id: string) => {
    setExpandedEntries((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const filteredBacklog = useMemo(() => {
    if (filterArchetype === 'all') return backlog;
    return backlog.filter((b) => b.contentArchetype === filterArchetype);
  }, [backlog, filterArchetype]);

  if (!isOpen) return null;

  return (
    <div className="drawer-overlay" onClick={onClose} role="presentation">
      <aside
        className="drawer-panel drawer-panel-wide"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Reading Backlog History"
      >
        <div className="drawer-header">
          <div className="drawer-title-group">
            <History size={18} className="drawer-icon-accent" />
            <span className="drawer-badge">TRANSMISSION-LOG</span>
            <h2 className="drawer-title">Reading Backlog ({backlog.length})</h2>
          </div>
          <button className="btn-close" onClick={onClose} aria-label="Close Backlog">
            <X size={20} />
          </button>
        </div>

        {/* Filter bar & Clear button */}
        <div className="drawer-subtoolbar">
          <div className="filter-pills">
            {['all', 'narrative', 'log', 'ledger', 'technical', 'clinical', 'codex'].map(
              (arch) => (
                <button
                  key={arch}
                  className={`filter-pill ${filterArchetype === arch ? 'active' : ''}`}
                  onClick={() => setFilterArchetype(arch)}
                >
                  {arch.toUpperCase()}
                </button>
              )
            )}
          </div>
          {backlog.length > 0 && (
            <button
              className="btn-danger-text"
              onClick={onClearBacklog}
              title="Clear entire backlog history"
            >
              <Trash2 size={14} />
              <span>Clear History</span>
            </button>
          )}
        </div>

        {/* Backlog content */}
        <div className="drawer-content scrollable">
          {filteredBacklog.length === 0 ? (
            <div className="drawer-empty-state">
              <p>NO TRANSMISSION HISTORY RECORDED IN THIS SECTOR.</p>
              <p className="empty-state-sub">
                Pages you visit will automatically log their prose and telemetry here.
              </p>
            </div>
          ) : (
            <div className="backlog-entries-list">
              {filteredBacklog.map((entry) => {
                const isExpanded = !!expandedEntries[entry.id];
                const isCurrent = entry.pageNumber === currentPage;
                const textParagraphs = entry.fullText || entry.text || [];

                return (
                  <div
                    key={entry.id}
                    className={`backlog-entry-card ${isCurrent ? 'entry-current' : ''}`}
                  >
                    <div className="backlog-entry-header">
                      <div className="entry-meta-group">
                        <span className="entry-page-badge">P. {entry.pageNumber}</span>
                        <span
                          className={`entry-archetype-tag archetype-${entry.contentArchetype}`}
                        >
                          {entry.contentArchetype.toUpperCase()}
                        </span>
                        <span className="entry-chapter-label">{entry.chapterTitle}</span>
                      </div>
                      <button
                        className="btn-jump-sm"
                        onClick={() => {
                          onJumpToPage(entry.pageNumber);
                          onClose();
                        }}
                      >
                        <span>Jump</span>
                        <ArrowRight size={13} />
                      </button>
                    </div>

                    <h4 className="entry-title">{entry.title}</h4>

                    <div className="entry-body">
                      {isExpanded && textParagraphs.length > 0 ? (
                        <div className="entry-fulltext">
                          {textParagraphs.map((p, idx) => (
                            <p
                              key={idx}
                              className={`entry-paragraph ${
                                entry.contentArchetype === 'log' ||
                                entry.contentArchetype === 'ledger'
                                  ? 'font-mono'
                                  : ''
                              }`}
                            >
                              {p}
                            </p>
                          ))}
                        </div>
                      ) : (
                        <p
                          className={`entry-snippet ${
                            entry.contentArchetype === 'log' ||
                            entry.contentArchetype === 'ledger'
                              ? 'font-mono'
                              : ''
                          }`}
                        >
                          {entry.textSnippet ||
                            (textParagraphs[0] ? textParagraphs[0].slice(0, 160) + '...' : '')}
                        </p>
                      )}
                    </div>

                    {textParagraphs.length > 1 && (
                      <button
                        className="btn-expand-toggle"
                        onClick={() => toggleExpand(entry.id)}
                      >
                        {isExpanded ? (
                          <>
                            <ChevronUp size={14} />
                            <span>Collapse Passage</span>
                          </>
                        ) : (
                          <>
                            <ChevronDown size={14} />
                            <span>Read Full Page ({textParagraphs.length} paragraphs)</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </aside>
    </div>
  );
};
